/**
 * User authentication class for frontend and backend components
 * Provides user information and authentication state
 * Supports both company users and workflow-specific users
 *
 * Works in both browser (frontend) and Deno (backend) environments
 */

// Environment detection
const isBrowser = typeof window !== "undefined";
const isDeno = typeof Deno !== "undefined";

// Get environment-specific values
function getApiUrl() {
    if (isBrowser) {
        const hostname = window?.location?.hostname ?? "";
        const isStaging = hostname.includes("staging-apps.agentui.ai") ||
            hostname.includes("staging-api.agentui.ai") ||
            hostname === "staging.agentui.ai";
        return hostname === "localhost" ||
            hostname?.includes("agentui.test")
            ? "http://localhost:3333"
            : isStaging
            ? "https://staging-api.agentui.ai"
            : "https://api.agentui.ai";
    } else if (isDeno) {
        return Deno.env.get("API_BASE_URL") || "https://api.agentui.ai";
    }
    return process.env.API_BASE_URL || "https://api.agentui.ai";
}

function getWorkflowId() {
    if (isBrowser) {
        return window.config?.workflowId;
    } else if (isDeno) {
        return Deno.env.get("WORKFLOW_ID");
    }
    return process.env.WORKFLOW_ID;
}

/**
 * Get workflowId dynamically (handles cases where window.config might not be ready at module load)
 * @returns {string|null} Workflow ID
 */
function getWorkflowIdDynamic() {
    const workflowId = getWorkflowId();
    // If workflowId is still undefined, try to find any stored token
    // This handles the case where workflowId wasn't available at module load
    if (!workflowId && isBrowser) {
        // Try to find a stored token key to infer workflowId
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith("workflow-auth-")) {
                const extractedId = key.replace("workflow-auth-", "");
                if (extractedId && extractedId !== "undefined") {
                    return extractedId;
                }
            }
        }
    }
    return workflowId;
}

/**
 * Get request context from the secure context map (Deno only)
 * This prevents credential leakage between concurrent requests
 */
function _getRequestContext() {
    if (!isDeno) return null;

    // Retrieve requestId from AsyncLocalStorage (set in server.ts)
    const storage = globalThis.__AGENTUI_REQUEST_ID_STORAGE__;
    const requestId = storage ? storage.getStore() : null;
    const contexts = globalThis.__AGENTUI_REQUEST_CONTEXTS__;

    if (!requestId || !contexts) return null;

    return contexts.get(requestId) || null;
}

function getApiKey() {
    if (isBrowser) {
        return window.config?.apiKey;
    } else if (isDeno) {
        // In Deno, get API key from secure request-scoped context
        const context = _getRequestContext();
        if (context?.apiKey) {
            return context.apiKey;
        }
        // Fall back to env var if no request context
        return Deno.env.get("AGENTUI_API_KEY") || null;
    }
    return process.env.AGENTUI_API_KEY;
}

function getAuthToken() {
    if (isBrowser) {
        const workflowId = getWorkflowId();
        if (workflowId) {
            return localStorage.getItem(`workflow-auth-${workflowId}`);
        }
        return null;
    } else if (isDeno) {
        // In Deno, get auth token from secure request-scoped context
        const context = _getRequestContext();
        return context?.authToken || null;
    }
    return null;
}

function isAuthError(error) {
    const msg = error?.message || "";
    return (
        /^HTTP 4\d{2}/.test(msg) ||
        /unauthorized|forbidden|invalid|session expired/i.test(msg)
    );
}

async function withRetry(fn, maxRetries = 2, baseDelayMs = 500) {
    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            if (isAuthError(error)) throw error;
            if (attempt >= maxRetries) break;
            const delay = baseDelayMs * Math.pow(2, attempt);
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }
    throw lastError;
}

// Build a per-request identity hash from auth-bearing headers. Two requests
// with the same method/url/body but different identities MUST NOT share a
// cache entry — otherwise user A's response leaks to user B in a co-located
// Node deployment.
function _authIdentity(headers) {
    if (!headers) return null;
    const parts = [
        headers.Authorization || headers.authorization,
        headers["x-token-auth"],
        headers["x-server-key"],
        headers["x-api-key"],
    ].filter(Boolean);
    return parts.length ? parts.join("|") : null;
}

// Deep-clone a coordinator response. structuredClone is universal in
// browser, Deno, and Node 17+. Falls back to JSON round-trip on older
// runtimes (covers our supported envs).
function _cloneResponse(value) {
    if (value == null || typeof value !== "object") return value;
    if (typeof structuredClone === "function") return structuredClone(value);
    return JSON.parse(JSON.stringify(value));
}

// Request coordinator: dedup + cache + retry, environment-aware.
//   Browser → process-wide singleton (single-user per tab).
//   Deno    → per-request via AsyncLocalStorage context (no cross-user leakage).
//   Node    → process-wide singleton (CLI / single-user scripts).
function createCoordinator() {
    const inflight = new Map();
    const positive = new Map();
    const negative = new Map();

    return {
        async request(method, url, fetchOpts, opts = {}) {
            const {
                cacheTtl = 300,
                negTtl = 5000,
                retries = 2,
                baseDelayMs = 500,
            } = opts;

            const sig = JSON.stringify({
                m: method.toUpperCase(),
                u: url,
                b: fetchOpts?.body || null,
                i: _authIdentity(fetchOpts?.headers),
            });
            const now = Date.now();

            const pos = positive.get(sig);
            // Clone on cache hit so mutations don't poison the stored value
            // or leak across callers (multi-tenant safety invariant).
            if (pos && now - pos.ts < cacheTtl) return _cloneResponse(pos.value);

            const neg = negative.get(sig);
            if (neg && now - neg.ts < negTtl) throw neg.error;

            if (inflight.has(sig)) {
                // Concurrent caller joining the inflight — also gets a clone
                // so the two callers can't share a mutable reference.
                return inflight.get(sig).then(_cloneResponse);
            }

            const promise = withRetry(
                () => User._executeRequest(method, url, fetchOpts),
                retries,
                baseDelayMs
            )
                .then((result) => {
                    if (
                        method.toUpperCase() === "GET" &&
                        result.status === 200
                    ) {
                        // Store an isolated copy so future callers always get
                        // pristine data even if the first caller mutates.
                        positive.set(sig, {
                            value: _cloneResponse(result),
                            ts: Date.now(),
                        });
                    }
                    return result;
                })
                .catch((error) => {
                    if (isAuthError(error)) {
                        negative.set(sig, { error, ts: Date.now() });
                    }
                    throw error;
                })
                .finally(() => {
                    inflight.delete(sig);
                });

            inflight.set(sig, promise);
            return promise;
        },
        clear() {
            inflight.clear();
            positive.clear();
            negative.clear();
        },
    };
}

function getCoordinator() {
    if (isBrowser) {
        if (!globalThis.__AGENTUI_REQ_COORD__) {
            globalThis.__AGENTUI_REQ_COORD__ = createCoordinator();
        }
        return globalThis.__AGENTUI_REQ_COORD__;
    }
    if (isDeno) {
        // Per-request coordinator — never share cache/inflight across users.
        const ctx = _getRequestContext();
        if (ctx) {
            if (!ctx._coordinator) ctx._coordinator = createCoordinator();
            return ctx._coordinator;
        }
        // No request context → no shared state, safest.
        return createCoordinator();
    }
    if (!globalThis.__AGENTUI_REQ_COORD__) {
        globalThis.__AGENTUI_REQ_COORD__ = createCoordinator();
    }
    return globalThis.__AGENTUI_REQ_COORD__;
}

class User {
    constructor() {
        // STATE is only recommended in Browser
        // In Deno, this singleton instance remains mostly stateless to prevent leakage
        this._user = null;
        this._isAuthenticated = false;
        this._token = null; // User's authentication token (from login)
        this._appToken = null; // App-level token (x_token_auth) - separate auth method
        this._initialized = false;

        this.apiUrl = getApiUrl();
        // Don't cache workflowId - read it dynamically
        this._cachedWorkflowId = null;

        // Check for app token in URL (browser only)
        if (isBrowser) {
            const urlParams = new URLSearchParams(window.location.search);
            this._appToken = urlParams.get("x_token_auth");

            // Expose system token for cross-app deep linking (createAppLink)
            if (this._appToken) {
                window.__systemToken = this._appToken;
            }

            // Handle cross-app deep link token (workflow-scoped JWT from redirect)
            const deepLinkToken = urlParams.get("x_deep_link_token");
            if (deepLinkToken) {
                // New session (admin impersonation / cross-app) — wipe the
                // previous user's persisted cache before it hydrates.
                this._wipeOfflineCache();
                this._token = deepLinkToken;
                this._hasDeepLinkToken = true;
                const wfId = getWorkflowIdDynamic();
                if (wfId) {
                    localStorage.setItem(`workflow-auth-${wfId}`, deepLinkToken);
                }
            }

            // Handle SSO callback. Two shapes are accepted:
            //
            //   ?x_sso_code=<opaque>  — current behavior. Backend issued a
            //     one-time exchange code instead of putting the JWT in the
            //     URL. We POST the code to /sso/exchange in init() to swap it
            //     for the JWT (so the JWT never touches Referer / browser
            //     history / proxy logs / 3rd-party scripts).
            //
            //   ?x_sso_token=<jwt>    — legacy. Old backends still embed the
            //     JWT in the URL. Kept so in-flight redirects against a
            //     pre-deploy backend continue to work; remove once the
            //     backend reliably emits only x_sso_code.
            //
            // x_sso_code wins when both are present.
            const ssoCode = urlParams.get("x_sso_code");
            const ssoToken = urlParams.get("x_sso_token");
            if (ssoCode) {
                // An exchange code means a new session is incoming. Wipe the
                // previous user's persisted cache NOW — waiting for the
                // exchange round-trip in _doInit() would let the provider
                // hydrate and briefly render the old user's data.
                this._wipeOfflineCache();
                this._pendingSSOCode = ssoCode;
                this._ssoTokenFromUrl = true;
                urlParams.delete("x_sso_code");
                urlParams.delete("x_sso_token");
                const cleanUrl = urlParams.toString()
                    ? `${window.location.pathname}?${urlParams.toString()}`
                    : window.location.pathname;
                window.history.replaceState({}, "", cleanUrl);
            } else if (ssoToken) {
                // New session via IdP redirect — wipe the previous user's
                // persisted cache before it hydrates.
                this._wipeOfflineCache();
                this._token = ssoToken;
                this._ssoTokenFromUrl = true;
                this._pendingSSOToken = ssoToken;
                const wfId = getWorkflowIdDynamic();
                if (wfId) {
                    localStorage.setItem(`workflow-auth-${wfId}`, ssoToken);
                    this._pendingSSOToken = null;
                }
                // Clean token from URL to prevent re-processing on refresh
                urlParams.delete("x_sso_token");
                const cleanUrl = urlParams.toString()
                    ? `${window.location.pathname}?${urlParams.toString()}`
                    : window.location.pathname;
                window.history.replaceState({}, "", cleanUrl);
            }

        }

        // Cache for me() method (transformed user data is reused for ~300ms)
        this._meCache = null;
        this._meCacheTimestamp = 0;

        // Bind instance methods to ensure `this` context is preserved
        // when methods are destructured or passed as callbacks
        this.me = this.me.bind(this);
        this.login = this.login.bind(this);
        this.logout = this.logout.bind(this);
        this.signup = this.signup.bind(this);
        this.refresh = this.refresh.bind(this);
        this.updateProfile = this.updateProfile.bind(this);
        this.init = this.init.bind(this);
        this.clearAuth = this.clearAuth.bind(this);
        this.clearCache = this.clearCache.bind(this);
        this.list = this.list.bind(this);
        this.filter = this.filter.bind(this);
        this.get = this.get.bind(this);
        this.findById = this.findById.bind(this);
        this.create = this.create.bind(this);
        this.update = this.update.bind(this);
        this.delete = this.delete.bind(this);
        this.schema = this.schema.bind(this);
        this.bulkCreate = this.bulkCreate.bind(this);
        this.requestPasswordReset = this.requestPasswordReset.bind(this);
        this.resetPassword = this.resetPassword.bind(this);
        this.ssoLogin = this.ssoLogin.bind(this);
        this.verifyMfa = this.verifyMfa.bind(this);
        this.requestMfaCode = this.requestMfaCode.bind(this);
    }

    // ============================================
    // Unified Request Executor
    // ============================================

    /**
     * Internal helper to execute fetch requests with robust parsing
     * Throws an error for non-2xx responses (matching dbAdapter/BaseEntity behavior)
     * @private
     */
    static async _executeRequest(method, url, options = {}) {
        const response = await fetch(url, options);

        // Handle 204 No Content
        if (response.status === 204) {
            return { data: null, status: response.status };
        }

        const text = await response.text();
        let responseData;
        try {
            responseData = text ? JSON.parse(text) : {};
        } catch (e) {
            // Handle non-JSON responses
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${text || "Unknown Error"}`);
            }
            responseData = {};
        }

        // For non-2xx responses, throw an error (matching dbAdapter behavior)
        if (!response.ok) {
            const errorMessage = responseData.error || responseData.message || `HTTP ${response.status}`;
            throw new Error(errorMessage);
        }

        return { data: responseData, status: response.status };
    }

    /**
     * Clear the result cache. Useful when forcing fresh data retrieval.
     */
    clearCache() {
        getCoordinator().clear();
        this._meCache = null;
        this._meCacheTimestamp = 0;
    }

    // ============================================
    // Static CRUD methods (admin/service role only)
    // ============================================

    /**
     * Get server key for service role authorization (Deno/Node only)
     * @private
     */
    static _getServerKey() {
        if (isBrowser) {
            return null;
        } else if (isDeno) {
            return Deno.env.get("AGENTUI_SERVER_KEY") || null;
        }
        return process.env.AGENTUI_SERVER_KEY || null;
    }

    /**
     * Make a static API call for admin/service operations.
     * Dedup, cache, and retry are handled by the request coordinator.
     * @private
     */
    static async _staticApiCall(method, path, data = null) {
        try {
            const apiUrl = getApiUrl();
            const url = `${apiUrl}/${path}`;
            const headers = { "Content-Type": "application/json" };

            const apiKey = getApiKey();
            if (apiKey) headers["x-api-key"] = apiKey;

            const serverKey = User._getServerKey();
            if (serverKey) headers["x-server-key"] = serverKey;

            const authToken = getAuthToken();
            if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

            const options = {
                method: method.toUpperCase(),
                headers,
            };

            if (data && method.toUpperCase() !== "GET") {
                options.body = JSON.stringify(data);
            }

            return await getCoordinator().request(method, url, options);
        } catch (error) {
            console.error(`Static API error (${method} ${path}):`, error);
            throw error;
        }
    }

    /**
     * List all workflow users (admin or service role only)
     * @param {string} sortField - Optional field to sort by (prefix with - for descending)
     * @param {number} limit - Optional limit on number of results
     * @param {number} offset - Optional offset for pagination (skip N records)
     * @returns {Promise<Array>} Array of user objects
     */
    async list(sortField = null, limit = null, offset = null) {
        const workflowId = getWorkflowId();
        let path = `workflows/${workflowId}/auth/users/list`;

        const queryParams = [];
        if (sortField)
            queryParams.push(`sortField=${encodeURIComponent(sortField)}`);
        if (limit) queryParams.push(`limit=${limit}`);
        if (offset) queryParams.push(`offset=${offset}`);
        if (queryParams.length > 0) {
            path += `?${queryParams.join("&")}`;
        }

        const response = await User._staticApiCall("GET", path);

        if (response.status !== 200) {
            throw new Error(response.data?.error || "Failed to list users");
        }

        return response.data?.data || [];
    }

    /**
     * Filter workflow users with conditions (admin or service role only)
     * Supports MongoDB-like operators: $regex, $gt, $gte, $lt, $lte, $ne, $in, $nin
     * @param {Object} conditions - Filter conditions
     * @param {string} sortField - Optional field to sort by (prefix with - for descending)
     * @param {number} limit - Optional limit on number of results
     * @param {number} offset - Optional offset for pagination (skip N records)
     * @returns {Promise<Array>} Array of matching user objects
     */
    async filter(conditions = {}, sortField = null, limit = null, offset = null) {
        const workflowId = getWorkflowId();
        const path = `workflows/${workflowId}/auth/users/filter`;

        const response = await User._staticApiCall("POST", path, {
            conditions,
            sortField,
            limit,
            offset,
        });

        if (response.status !== 200) {
            throw new Error(response.data?.error || "Failed to filter users");
        }

        return response.data?.data || [];
    }

    /**
     * Count workflow users, optionally filtered by conditions (admin or service role only)
     * Mirrors BaseEntity.count() — returns a plain number.
     * @param {Object|null} conditions - Optional filter conditions (same syntax as filter)
     * @returns {Promise<number>} Count of matching users
     */
    async count(conditions = null) {
        const workflowId = getWorkflowId();
        const path = `workflows/${workflowId}/auth/users/count`;

        const response = await User._staticApiCall("POST", path, {
            ...(conditions ? { conditions } : {}),
        });

        if (response.status !== 200) {
            throw new Error(
                response.data?.error || "Failed to count users"
            );
        }

        return response.data?.data ?? 0;
    }

    /**
     * Get a workflow user by ID (admin or service role only)
     * @param {string} id - User ID to fetch
     * @returns {Promise<Object>} User object
     */
    async get(id) {
        return this.findById(id);
    }

    /**
     * Find a workflow user by ID (admin or service role only)
     * @param {string} id - User ID to fetch
     * @returns {Promise<Object>} User object
     */
    async findById(id) {
        const workflowId = getWorkflowId();
        const path = `workflows/${workflowId}/auth/users/get/${id}`;

        const response = await User._staticApiCall("GET", path);

        if (response.status !== 200) {
            throw new Error(response.data?.error || "Failed to get user");
        }

        return response.data?.data || null;
    }

    /**
     * Create a new workflow user (admin or service role only)
     * @param {Object} data - User data
     * @param {string} data.email - User's email (required)
     * @param {string} data.password - User's password (required)
     * @param {string} data.firstName - User's first name (required)
     * @param {string} data.lastName - User's last name (required)
     * @param {string} data.username - User's username (optional)
     * @param {Array} data.roles - User's roles (optional, defaults to ['user'])
     * @param {Object} data.config - Custom config object (optional)
     * @returns {Promise<Object>} Created user object
     */
    async create(data) {
        // In Deno (backend functions), skip the client-side role check.
        // The server enforces permissions via x-server-key (service role).
        // In browser, only admins can create users directly.
        if (!isDeno && this.role !== "admin") {
            const suggestion = "User.create() requires admin privileges. Turn on Backend Functions in your app settings, then create a backend function (e.g. createUser) that calls User.create() from the server side where it runs with service-level permissions.";
            if (typeof console.human === "function") {
                console.human(suggestion, {
                    type: "permission_suggestion",
                    entity: "User",
                    operation: "create",
                    suggestion,
                    actionable: true,
                });
            }
            throw new Error("Only administrators can create users. Use a backend function to create users from non-admin contexts.");
        }

        // Clear cache on write operation
        this.clearCache();

        const workflowId = getWorkflowId();
        const path = `workflows/${workflowId}/auth/users/create`;

        // Backend handles fullName -> firstName + lastName conversion
        // Backend also handles custom fields -> config

        const response = await User._staticApiCall("POST", path, data);

        if (response.status !== 200) {
            throw new Error(response.data?.error || "Failed to create user");
        }

        return response.data?.data || null;
    }

    /**
     * Bulk create workflow users (admin or service role only)
     * @param {Array} usersData - Array of user data objects
     * @returns {Promise<Object>} Result with created users and any errors
     */
    async bulkCreate(usersData) {
        if (!isDeno && this.role !== "admin") {
            const suggestion = "User.bulkCreate() requires admin privileges. Turn on Backend Functions in your app settings, then create a backend function (e.g. bulkCreateUsers) that calls User.bulkCreate() from the server side where it runs with service-level permissions.";
            if (typeof console.human === "function") {
                console.human(suggestion, {
                    type: "permission_suggestion",
                    entity: "User",
                    operation: "bulkCreate",
                    suggestion,
                    actionable: true,
                });
            }
            throw new Error("Only administrators can create users. Use a backend function to create users from non-admin contexts.");
        }

        // Clear cache on write operation
        this.clearCache();

        const workflowId = getWorkflowId();
        const path = `workflows/${workflowId}/auth/users/bulk-create`;

        // Backend handles fullName -> firstName + lastName conversion
        // Backend also handles custom fields -> config

        const response = await User._staticApiCall("POST", path, {
            users: usersData,
        });

        if (response.status !== 200 && !response.data?.data) {
            throw new Error(
                response.data?.error || "Failed to bulk create users"
            );
        }

        return {
            users: response.data?.data || [],
            errors: response.data?.errors,
            meta: response.data?.meta,
        };
    }

    /**
     * Update a workflow user (admin or service role only, or self-update)
     * Can be called as:
     * - User.update(id, data) - Update a specific user by ID
     * - User.update(data) - Update the current authenticated user
     * @param {string|Object} idOrData - User ID or update data
     * @param {Object} optionalData - Update data (when first arg is ID)
     * @returns {Promise<Object>} Updated user object
     */
    async update(idOrData, optionalData = null) {
        // Clear me() cache on user update
        this._meCache = null;
        this._meCacheTimestamp = 0;
        // Clear cache on write operation
        this.clearCache();

        const workflowId = getWorkflowId();
        const path = `workflows/${workflowId}/auth/users/update`;

        let data;
        if (typeof idOrData === "string") {
            // Called as update(id, data)
            data = { id: idOrData, ...optionalData };
        } else if (typeof idOrData === "object" && idOrData !== null) {
            // Called as update(data) - update current user
            data = idOrData;
        } else {
            throw new Error(
                "Invalid parameters: expected (id, data) or (data)"
            );
        }

        const response = await User._staticApiCall("PUT", path, data);

        if (response.status !== 200) {
            throw new Error(response.data?.error || "Failed to update user");
        }

        return response.data?.data || null;
    }

    /**
     * Delete a workflow user (admin or service role only)
     * @param {string} id - User ID to delete
     * @returns {Promise<boolean>} True if deleted successfully
     */
    async delete(id) {
        // Clear cache on write operation
        this.clearCache();

        const workflowId = getWorkflowId();
        const path = `workflows/${workflowId}/auth/users/delete/${id}`;

        const response = await User._staticApiCall("DELETE", path);

        if (response.status !== 200) {
            throw new Error(response.data?.error || "Failed to delete user");
        }

        return true;
    }

    /**
     * Invite a user by email. Creates the user and sends a password reset email.
     * Any authenticated user can invite with role "user".
     * Only admins (or service role in Deno) can invite with role "admin".
     * @param {string} email - Email of the user to invite
     * @param {string} role - Role to assign: "user" (default) or "admin"
     * @param {Object} options - Optional: { firstName, lastName }
     * @returns {Promise<Object>} Invited user object with { id, email, firstName, lastName, roles, invited: true }
     */
    async inviteUser(email, role = "user", options = {}) {
        const workflowId = getWorkflowId();
        const path = `workflows/${workflowId}/auth/users/invite`;

        const response = await User._staticApiCall("POST", path, {
            email,
            role,
            ...(options.firstName ? { firstName: options.firstName } : {}),
            ...(options.lastName ? { lastName: options.lastName } : {}),
        });

        if (response.status !== 200) {
            throw new Error(
                response.data?.error || "Failed to invite user"
            );
        }

        return response.data?.data || null;
    }

    /**
     * Get the workflow user schema (admin or service role only)
     * Returns combined schema with base WorkflowUser fields + custom entity fields from User.json component
     * @returns {Object} Schema object with field definitions
     */
    async schema() {
        const workflowId = getWorkflowId();
        const path = `workflows/${workflowId}/auth/users/schema`;

        const response = await User._staticApiCall("GET", path);

        if (response.status !== 200) {
            throw new Error(response.data?.error || "Failed to get schema");
        }

        return response.data?.data?.schema || {};
    }

    // ============================================
    // Instance methods
    // ============================================

    /**
     * Make an API call. Dedup, cache, and retry are handled by the request
     * coordinator (singleton in browser/node, per-request in Deno).
     * @private
     */
    async _apiCall(method, path, data = null, customHeaders = {}) {
        try {
            const url = `${this.apiUrl}/${path}`;
            const headers = {
                "Content-Type": "application/json",
                ...customHeaders,
            };

            const apiKey = getApiKey();
            if (apiKey) headers["x-api-key"] = apiKey;

            const authToken = getAuthToken();
            if (authToken && !headers["Authorization"]) {
                headers["Authorization"] = `Bearer ${authToken}`;
            }

            if (this._appToken) {
                headers["x-token-auth"] = this._appToken;
            }

            const options = {
                method: method.toUpperCase(),
                headers,
            };

            if (data && method.toUpperCase() !== "GET") {
                options.body = JSON.stringify(data);
            }

            return await getCoordinator().request(method, url, options);
        } catch (error) {
            console.error(`API error (${method} ${path}):`, error);
            throw error;
        }
    }

    /**
     * Initialize user authentication (Browser only)
     * In Deno, this is effectively a no-op as state is request-scoped
     */
    async init() {
        if (isDeno) return null;

        if (this._initialized) {
            return this._user;
        }

        // If init is already in progress, return the existing promise
        if (this._initPromise) {
            return this._initPromise;
        }

        this._initPromise = this._doInit();
        return this._initPromise;
    }

    async _doInit() {

        try {
            // If app token (x_token_auth) is in URL, use it to login
            // Skip if a deep link token is present — it takes priority (admin impersonation)
            if (this._appToken && !this._hasDeepLinkToken) {
                await this.login(null, null);
            }

            // Redeem one-time SSO exchange code (?x_sso_code=...) for a JWT.
            // The code is opaque and worthless after consume, so the network
            // round-trip is the only place the actual JWT exists outside this
            // tab's localStorage — never in the URL, browser history, or
            // proxy logs. Failure clears auth so the user lands on /login.
            if (this._pendingSSOCode) {
                const wfId = getWorkflowIdDynamic();
                if (wfId) {
                    try {
                        const result = await this._exchangeSSOCode(
                            wfId,
                            this._pendingSSOCode
                        );
                        if (result?.token) {
                            // New session via SSO exchange — wipe the previous
                            // user's persisted cache before it hydrates.
                            this._wipeOfflineCache();
                            this._token = result.token;
                            this._user = result.user || null;
                            this._isAuthenticated = !!result.user;
                            localStorage.setItem(`workflow-auth-${wfId}`, result.token);
                            if (this._user) {
                                localStorage.setItem(
                                    `user-data-${this.tokenKey}`,
                                    JSON.stringify(this._user)
                                );
                            }
                        }
                    } catch (exchangeErr) {
                        console.error("SSO exchange failed:", exchangeErr);
                        this.clearAuth();
                    }
                }
                this._pendingSSOCode = null;
            }

            // If SSO token was captured from URL but couldn't be stored yet, store it now
            if (this._pendingSSOToken) {
                const wfId = getWorkflowIdDynamic();
                if (wfId) {
                    localStorage.setItem(`workflow-auth-${wfId}`, this._pendingSSOToken);
                    this._pendingSSOToken = null;
                }
            }

            // Don't overwrite token if it was set from SSO/deep-link URL params
            if (!this._token) {
                this._token = localStorage.getItem(this.tokenKey);
            }

            if (this._token) {
                // Fetch user profile
                try {
                    const response = await this._fetchWorkflowUser();

                    if (response.data?.data?.user) {
                        this._user = response.data.data.user;
                        this._isAuthenticated = true;

                        // Store user data for offline access
                        localStorage.setItem(
                            `user-data-${this.tokenKey}`,
                            JSON.stringify(this._user)
                        );

                        // If authenticated via SSO token from URL, redirect from /login to /
                        if (this._ssoTokenFromUrl && window.location.pathname === "/login") {
                            this._ssoTokenFromUrl = false;
                            window.location.replace("/");
                        }
                    } else {
                        this.clearAuth();
                    }
                } catch (fetchError) {
                    // Only use cached data when:
                    // 1. Offline mode is explicitly enabled for this app
                    // 2. The error is a genuine network failure (not a 401/403 rejection)
                    const offlineEnabled = isBrowser && window.config?.isOfflineEnabled;
                    const isNetworkError = fetchError instanceof TypeError ||
                        (isBrowser && !navigator.onLine);
                    if (offlineEnabled && isNetworkError) {
                        const cachedUser = this.getCachedUser();
                        if (cachedUser) {
                            console.warn("Offline: using cached user data");
                            this._user = cachedUser;
                            this._isAuthenticated = true;
                        } else {
                            this.clearAuth();
                        }
                    } else {
                        // Server rejected auth, or offline mode not enabled — clear session
                        this.clearAuth();
                    }
                }
            }

            this._initialized = true;
            return this._user;
        } catch (error) {
            console.error("User authentication failed:", error);
            const offlineEnabled = isBrowser && window.config?.isOfflineEnabled;
            const isNetworkError = error instanceof TypeError ||
                (isBrowser && !navigator.onLine);
            if (offlineEnabled && isNetworkError) {
                const cachedUser = this.getCachedUser();
                if (cachedUser && this._token) {
                    console.warn("Offline: using cached user data");
                    this._user = cachedUser;
                    this._isAuthenticated = true;
                    this._initialized = true;
                    return this._user;
                }
            }
            this.clearAuth();
            this._initialized = true;
            return null;
        }
    }

    /**
     * Redeem a one-time workflow SSO exchange code for a JWT. Used only on
     * the post-IdP-redirect bootstrap when ?x_sso_code=... is present in the
     * URL — the backend issued the opaque code in lieu of the JWT to keep
     * the JWT off the URL.
     * @private
     */
    async _exchangeSSOCode(workflowId, code) {
        const url = `${this.apiUrl}/workflows/${workflowId}/auth/sso/exchange`;
        const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code }),
        });
        const text = await response.text();
        let body = {};
        try {
            body = text ? JSON.parse(text) : {};
        } catch {
            body = {};
        }
        if (!response.ok) {
            const msg = body?.error || body?.message || `HTTP ${response.status}`;
            throw new Error(msg);
        }
        return {
            token: body?.token || null,
            user: body?.user || null,
            expiresIn: body?.expiresIn || null,
        };
    }

    /**
     * Fetch workflow user profile
     * @private
     */
    async _fetchWorkflowUser() {
        // Get token dynamically to support request-scoped contexts in Deno
        const token = getAuthToken() || this._token;
        return this._apiCall(
            "get",
            `workflows/${this.workflowId}/auth/me`,
            {},
            {
                Authorization: `Bearer ${token}`,
            }
        );
    }

    getToken() {
        // Get token dynamically to support request-scoped contexts in Deno
        return getAuthToken() || this._token;
    }

    /**
     * Get current user data (cached for 300ms)
     * @returns {Object|null} User object with role property or null if not authenticated
     */
    async me() {
        try {
            // Safety check: ensure instance is properly initialized
            if (typeof this === 'undefined' || this === null || typeof this._meCache === 'undefined') {
                console.warn('User.me() called with invalid context');
                return null;
            }

            // Browser: wait for init() to settle so we never race against
            // _doInit's token rehydration / SSO-callback handling.
            if (isBrowser && !this._initialized) {
                await this.init().catch(() => {});
            }

            const token = getAuthToken() || (!isDeno ? this._token : null);
            if (!token) return null;

            // Check cache (300ms). Browser only: in Deno this singleton is
            // shared by every concurrent request, so a 300 ms memo handed user
            // A's identity to user B (custom MCP tools made that interleaving
            // routine). The per-request coordinator still dedupes in Deno.
            const now = Date.now();
            if (!isDeno && this._meCache && now - this._meCacheTimestamp < 300) {
                return this._meCache;
            }

            const response = await this._fetchWorkflowUser();

            if (response.status === 200 && response.data?.data?.user) {
                const userData = response.data.data.user;

                if (isBrowser) {
                    this._user = userData;
                    this._isAuthenticated = true;
                    localStorage.setItem(
                        `user-data-${this.tokenKey}`,
                        JSON.stringify(this._user)
                    );
                }

                // Return user data with role property
                const result = {
                    ...userData,
                    fullName: `${userData.firstName} ${userData.lastName}`,
                    roles: userData.roles || [],
                    isAuthenticated: true,
                };

                // Cache the result
                this._meCache = result;
                this._meCacheTimestamp = now;

                return result;
            } else {
                if (isBrowser) this.clearAuth();
                this._meCache = null;
                return null;
            }
        } catch (error) {
            console.error("Failed to refresh user data:", error);
            const offlineEnabled = isBrowser && window.config?.isOfflineEnabled;
            const isNetworkError = error instanceof TypeError ||
                (isBrowser && !navigator.onLine);
            if (offlineEnabled && isNetworkError && isBrowser) {
                if (this._user) return this._user;
                const cached = this.getCachedUser();
                if (cached) {
                    this._user = cached;
                    this._isAuthenticated = true;
                    return {
                        ...cached,
                        fullName: `${cached.firstName} ${cached.lastName}`,
                        roles: cached.roles || [],
                        isAuthenticated: true,
                    };
                }
            }
            return null;
        }
    }

    /**
     * Get workflowId dynamically (handles cases where window.config might not be ready at module load)
     * @returns {string|null} Workflow ID
     */
    get workflowId() {
        const workflowId = getWorkflowIdDynamic();
        if (workflowId) {
            this._cachedWorkflowId = workflowId;
        }
        return workflowId || this._cachedWorkflowId;
    }

    /**
     * Get token key for localStorage (dynamic based on current workflowId)
     * @returns {string} Token key
     */
    get tokenKey() {
        const workflowId = this.workflowId;
        if (!workflowId) {
            // Fallback: try to find existing token key
            if (isBrowser) {
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith("workflow-auth-")) {
                        const extractedId = key.replace("workflow-auth-", "");
                        if (extractedId && extractedId !== "undefined") {
                            this._cachedWorkflowId = extractedId;
                            return key;
                        }
                    }
                }
            }
            // Last resort: use a default key
            return "workflow-auth-default";
        }
        return `workflow-auth-${workflowId}`;
    }

    /**
     * Check if user is authenticated
     * @returns {boolean} Authentication status
     */
    get isAuthenticated() {
        return this._isAuthenticated && this._user !== null;
    }

    /**
     * Get user roles
     * @returns {Array} Array of user roles
     */
    get roles() {
        return this._user?.roles || [];
    }

    /**
     * Get user's primary role (admin or user)
     * @returns {string} 'admin' if user has admin or super admin role, otherwise 'user'
     */
    get role() {
        if (!this._user) {
            return "user";
        }

        // Check if user has admin or super admin role
        const hasAdminRole = (this._user.roles || []).some(
            (role) =>
                role.toLowerCase() === "admin" ||
                role.toLowerCase() === "super admin" ||
                role.toLowerCase() === "superadmin"
        );

        return hasAdminRole ? "admin" : "user";
    }

    /**
     * Get the user's primary role VERBATIM, including custom roles.
     * Unlike `role` (which collapses to only 'admin'/'user'), this returns the
     * actual first role — e.g. 'manager', 'editor'. Use it to gate on a custom
     * role: `User.primaryRole === 'manager'` (or `User.hasRole('manager')`).
     * @returns {string} The first role, or 'user' if none.
     */
    get primaryRole() {
        return this._user?.roles?.[0] || "user";
    }

    /**
     * Get user's full name
     * @returns {string} Full name
     */
    get fullName() {
        if (!this._user) return "";
        return `${this._user.firstName || ""} ${
            this._user.lastName || ""
        }`.trim();
    }

    /**
     * Get user's first name
     * @returns {string} First name
     */
    get firstName() {
        return this._user?.firstName || "";
    }

    /**
     * Get user's last name
     * @returns {string} Last name
     */
    get lastName() {
        return this._user?.lastName || "";
    }

    /**
     * Get user's email
     * @returns {string} Email address
     */
    get email() {
        return this._user?.email || "";
    }

    /**
     * Get user's groups
     * @returns {Array} Array of user groups
     */
    get groups() {
        return this._user?.groups || [];
    }

    /**
     * Get user's company information
     * @returns {Object|null} Company object
     */
    get company() {
        return this._user?.company || null;
    }

    /**
     * Get user's ID
     * @returns {string|null} User ID
     */
    get id() {
        return this._user?.id || null;
    }

    /**
     * Check if user has a specific role
     * @param {string} role - Role to check
     * @returns {boolean} True if user has the role
     */
    hasRole(role) {
        return this._user?.roles?.includes(role) || false;
    }

    /**
     * Check if user belongs to a specific group
     * @param {string} groupId - Group ID to check
     * @returns {boolean} True if user belongs to the group
     */
    belongsToGroup(groupId) {
        return this.groups.some(
            (group) => group.id === groupId || group === groupId
        );
    }

    /**
     * Refresh user data from API
     * @returns {Object|null} Updated user object
     */
    async refresh() {
        // Clear me() cache to force fresh fetch
        this._meCache = null;
        this._meCacheTimestamp = 0;
        try {
            const token = getAuthToken() || (!isDeno ? this._token : null);
            if (!token) return null;

            const response = await this._fetchWorkflowUser();

            if (response.data && response.data.data?.user) {
                const userData = response.data.data.user;

                if (isBrowser) {
                    this._user = userData;
                    this._isAuthenticated = true;
                    localStorage.setItem(
                        `user-data-${this.tokenKey}`,
                        JSON.stringify(this._user)
                    );
                }
                return userData;
            } else {
                if (isBrowser) this.clearAuth();
                return null;
            }
        } catch (error) {
            console.error("Failed to refresh user data:", error);
            const offlineEnabled = isBrowser && window.config?.isOfflineEnabled;
            const isNetworkError = error instanceof TypeError ||
                (isBrowser && !navigator.onLine);
            if (offlineEnabled && isNetworkError && isBrowser) {
                return this._user || this.getCachedUser();
            }
            return null;
        }
    }

    /**
     * Update user profile fields (self-update only)
     * For admin updates of other users, use the static User.update(id, data) method
     * @param {Object} updates - Fields to update
     * @returns {Object} Update response
     */
    async updateProfile(updates) {
        // Clear me() cache on profile update
        this._meCache = null;
        this._meCacheTimestamp = 0;
        try {
            if (isBrowser) this.clearCache();

            const token = getAuthToken() || (!isDeno ? this._token : null);
            if (!token) throw new Error("No authentication token found");

            let dataToSend = { ...updates };

            // Handle fullName
            if (dataToSend.fullName) {
                const nameParts = dataToSend.fullName.trim().split(/\s+/);
                dataToSend.firstName = nameParts[0];
                if (nameParts.length > 1) {
                    dataToSend.lastName = nameParts.slice(1).join(" ");
                }
            }

            const response = await this._apiCall(
                "put",
                `workflows/${this.workflowId}/auth/profile`,
                dataToSend,
                { Authorization: `Bearer ${token}` }
            );

            if (response.data && response.data.data?.success !== false) {
                const userData = response.data.data.user || response.data.data;

                if (isBrowser) {
                    this._user = userData;
                    this._isAuthenticated = true;
                    localStorage.setItem(
                        `user-data-${this.tokenKey}`,
                        JSON.stringify(this._user)
                    );
                }
                return response;
            } else {
                throw new Error(
                    response.data?.message || "Failed to update profile"
                );
            }
        } catch (error) {
            console.error("Failed to update profile:", error);
            throw error;
        }
    }

    /**
     * Wipe the persisted offline query cache (IndexedDB) plus the in-memory
     * React Query cache. The cache key is scoped per WORKFLOW, not per user —
     * so EVERY path that installs a new session token must call this, or the
     * next user on a shared device hydrates the previous user's cached data
     * (profile, entity records) on boot. Hook is installed by main.jsx when
     * offline mode is enabled; best-effort, must never block auth.
     */
    _wipeOfflineCache() {
        if (!isBrowser) return;
        try {
            if (window.__clearOfflineCache) {
                window.__clearOfflineCache();
            } else {
                // Constructor-time paths (deep-link / SSO URL tokens) run at
                // module import, before main.jsx installs the hook. Queue the
                // wipe; main.jsx honors it right after installing.
                window.__pendingOfflineWipe = true;
            }
        } catch {
            /* noop */
        }
    }

    /**
     * Clear authentication data
     */
    clearAuth() {
        // Wipe coordinator caches so a previous session's 200 (or cached
        // 401) can't be replayed against a new session — env-independent.
        this.clearCache();

        // Session state — env-independent so Node/Deno don't leak after logout.
        this._user = null;
        this._isAuthenticated = false;
        this._token = null;

        if (isBrowser) {
            localStorage.removeItem(this.tokenKey);
            localStorage.removeItem(`user-data-${this.tokenKey}`);
            this._wipeOfflineCache();
        }
    }

    /**
     * Login with email/username and password
     * Throws an error if authentication fails (matching dbAdapter/BaseEntity behavior)
     * @param {string} emailOrUsername - User email or username
     * @param {string} password - User password
     * @returns {Object} Login response with user, token, id at top level
     * @throws {Error} If authentication fails
     */
    async login(emailOrUsername, password) {
        // Clear me() cache on login
        this._meCache = null;
        this._meCacheTimestamp = 0;
        if (isBrowser) this.clearCache();

        const loginData = {
            emailOrUsername: emailOrUsername?.trim(),
            password,
        };

        if (this._appToken) {
            loginData.appToken = this._appToken;
        }

        const response = await this._apiCall(
            "post",
            `workflows/${this.workflowId}/auth/login`,
            loginData
        );

        // Handle MFA challenge — auto-send code, don't store token yet
        if (response.data?.mfaRequired) {
            const challengeToken = response.data.mfaChallengeToken;
            const userEmail = response.data.email;

            // Automatically request the MFA code so the user receives it immediately
            try {
                await this.requestMfaCode(challengeToken, userEmail);
            } catch (e) {
                console.error("Failed to auto-send MFA code:", e);
            }

            return {
                ...response,
                mfaRequired: true,
                mfaChallengeToken: challengeToken,
                email: userEmail,
            };
        }

        const userData = response.data?.user;
        const token = response.data?.token;

        if (token) {
            if (isBrowser) {
                // New session on this device — wipe the previous user's
                // persisted cache before this session starts querying.
                this._wipeOfflineCache();
                this._token = token;
                this._user = userData;
                this._isAuthenticated = true;
                localStorage.setItem(this.tokenKey, this._token);
                localStorage.setItem(
                    `user-data-${this.tokenKey}`,
                    JSON.stringify(this._user)
                );
            }
        }

        // Return flattened response
        return {
            ...response,
            user: userData,
            token: token,
            id: userData?.id,
        };
    }

    /**
     * Redirect to SSO login for this workflow
     * The IdP redirects back with x_sso_code (one-time exchange code) which
     * the User class redeems for a JWT automatically. Older backends still
     * emit x_sso_token (raw JWT in URL) and that path is also handled.
     */
    ssoLogin() {
        if (!isBrowser) return;
        const workflowId = this.workflowId;
        if (!workflowId) throw new Error("Workflow ID is required for SSO login");
        window.location.href = `${this.apiUrl}/workflows/${workflowId}/auth/sso/login`;
    }

    /**
     * Check if SSO is enabled for this workflow
     * @returns {boolean}
     */
    get isSSOEnabled() {
        if (isBrowser) {
            return window.config?.ssoEnabled || false;
        }
        return false;
    }

    /**
     * Check if MFA is enabled for this workflow
     * @returns {boolean}
     */
    get isMFAEnabled() {
        if (isBrowser) {
            return window.config?.mfaEnabled || false;
        }
        return false;
    }

    /**
     * Verify an MFA code after login returns mfaRequired: true
     * @param {string} mfaChallengeToken - The challenge token from login response
     * @param {string} code - The 6-digit verification code
     * @returns {Object} Login response with user, token, id at top level
     * @throws {Error} If verification fails
     */
    async verifyMfa(mfaChallengeToken, code) {
        const response = await this._apiCall(
            "post",
            `workflows/${this.workflowId}/auth/mfa/verify`,
            { mfaChallengeToken, code }
        );

        const userData = response.data?.user;
        const token = response.data?.token;

        if (token) {
            if (isBrowser) {
                // New session on this device — wipe the previous user's
                // persisted cache before this session starts querying.
                this._wipeOfflineCache();
                this._token = token;
                this._user = userData;
                this._isAuthenticated = true;
                localStorage.setItem(this.tokenKey, this._token);
                localStorage.setItem(
                    `user-data-${this.tokenKey}`,
                    JSON.stringify(this._user)
                );
            }
        }

        return {
            ...response,
            user: userData,
            token: token,
            id: userData?.id,
        };
    }

    /**
     * Request a new MFA code (resend)
     * @param {string} mfaChallengeToken - The challenge token from login response
     * @param {string} email - The user's email
     * @returns {Object} Response with status and message
     */
    async requestMfaCode(mfaChallengeToken, email) {
        return await this._apiCall(
            "post",
            `workflows/${this.workflowId}/auth/mfa/request`,
            { mfaChallengeToken, email }
        );
    }

    /**
     * Sign up a new workflow user (only for workflow auth)
     * Throws an error if signup fails (matching dbAdapter/BaseEntity behavior)
     * @param {Object} params - Signup parameters
     * @param {string} params.username - Username
     * @param {string} params.email - Email
     * @param {string} params.password - Password
     * @param {string} params.firstName - First name
     * @param {string} params.lastName - Last name
     * @returns {Object} Signup response with user, token, id at top level
     * @throws {Error} If signup fails
     */
    async signup({ username, email, password, firstName, lastName }) {
        // If user is authenticated but not admin, suggest using a backend function
        if (this._isAuthenticated && this.role !== "admin") {
            const suggestion = "User.signup() is being called while a non-admin user is logged in. To create users from non-admin contexts, turn on Backend Functions and create a backend function that calls User.create() server-side where it runs with service-level permissions.";
            if (typeof console.human === "function") {
                console.human(suggestion, {
                    type: "permission_suggestion",
                    entity: "User",
                    operation: "signup",
                    suggestion,
                    actionable: true,
                });
            }
        }

        // Clear me() cache on signup
        this._meCache = null;
        this._meCacheTimestamp = 0;
        if (isBrowser) this.clearCache();

        const response = await this._apiCall(
            "post",
            `workflows/${this.workflowId}/auth/signup`,
            { username, email, password, firstName, lastName }
        );

        const userData = response.data?.user;
        const token = response.data?.token;

        // Only store the new token if not already authenticated
        // (e.g. admin creating a user should keep their own session)
        if (token && !this._isAuthenticated) {
            if (isBrowser) {
                // New session on this device — wipe the previous user's
                // persisted cache before this session starts querying.
                this._wipeOfflineCache();
                this._token = token;
                this._user = userData;
                this._isAuthenticated = true;
                localStorage.setItem(this.tokenKey, this._token);
                localStorage.setItem(
                    `user-data-${this.tokenKey}`,
                    JSON.stringify(this._user)
                );
            }
        }

        // Return flattened response
        return {
            ...response,
            user: userData,
            token: token,
            id: userData?.id,
        };
    }

    /**
     * Logout user
     */
    async logout() {
        // Clear me() cache on logout
        this._meCache = null;
        this._meCacheTimestamp = 0;
        try {
            if (isBrowser) this.clearCache();

            await this._apiCall(
                "post",
                `workflows/${this.workflowId}/auth/logout`,
                {}
            );
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            if (isBrowser) this.clearAuth();
        }
    }

    /**
     * Request a password reset link
     * @param {string} emailOrUsername - User's email or username
     * @returns {Object} Response indicating success or failure
     */
    async requestPasswordReset(emailOrUsername) {
        try {
            const response = await this._apiCall(
                "post",
                `workflows/${this.workflowId}/auth/request-password-reset`,
                {
                    emailOrUsername: emailOrUsername.trim(),
                }
            );

            return response;
        } catch (error) {
            console.error("Password reset request failed:", error);
            throw error;
        }
    }

    /**
     * Reset password with token
     * @param {string} token - Reset token from email
     * @param {string} newPassword - New password to set
     * @returns {Object} Response indicating success or failure
     */
    async resetPassword(token, newPassword) {
        // Clear me() cache on password reset
        this._meCache = null;
        this._meCacheTimestamp = 0;
        try {
            // Clear cache on write operation
            this.clearCache();

            const response = await this._apiCall(
                "post",
                `workflows/${this.workflowId}/auth/reset-password`,
                {
                    token,
                    newPassword,
                }
            );

            return response;
        } catch (error) {
            console.error("Password reset failed:", error);
            throw error;
        }
    }

    /**
     * Get user data from localStorage (for offline access) - browser only
     * @returns {Object|null} Cached user data
     */
    getCachedUser() {
        if (!isBrowser) return null;

        try {
            const cachedData = localStorage.getItem(
                `user-data-${this.tokenKey}`
            );
            return cachedData ? JSON.parse(cachedData) : null;
        } catch (error) {
            console.error("Failed to parse cached user data:", error);
            return null;
        }
    }
}

// Create singleton instance
const userInstance = new User();

// Initialize on load if token exists (browser only)
if (isBrowser) {
    // Auto-init if user has a stored auth token, x_token_auth, an SSO/deep-link
    // token from URL, or a pending SSO exchange code that still needs to be
    // redeemed for a JWT.
    const storedToken = localStorage.getItem(userInstance.tokenKey);
    const hasAppToken = userInstance._appToken !== null;
    const hasURLToken = userInstance._token !== null;
    const hasSSOCode = userInstance._pendingSSOCode != null;

    if (storedToken || hasAppToken || hasURLToken || hasSSOCode) {
        userInstance.init().catch((error) => {
            console.error("Failed to initialize user authentication:", error);
        });
    }
}

// Named exports — primarily for testing and advanced consumers.
export {
    User,
    isAuthError,
    withRetry,
    createCoordinator,
    getCoordinator,
};

export default userInstance;
